/**
 * Frontend activity log — in-memory store + subscribe pattern.
 *
 * Mantiene las últimas N entradas del runtime del navegador. Cada request
 * HTTP pasa por acá (via wrapper en `api.js`), y además hay handlers para:
 *   - window.onerror          → errores sincrónicos globales
 *   - unhandledrejection      → promesas no manejadas
 *   - console.error/warn      → mensajes de librerías que no lanzan
 *
 * Los componentes React usan `useDevLog()` para recibir actualizaciones en
 * vivo. Cada entrada también se envía al backend (POST /api/dev/logs) para
 * que el panel pueda unificar backend+frontend en una misma vista.
 *
 * Formato:
 *   {
 *     id:        número incremental,
 *     at:        ISO timestamp,
 *     source:    'frontend',
 *     level:     'info' | 'warn' | 'error',
 *     phase:     'request' | 'response' | 'error' | 'log' | 'console',
 *     message:   string,
 *     component: string (módulo o "global"),
 *     method:    string  (sólo http),
 *     path:      string  (sólo http),
 *     status:    number  (sólo response),
 *     durationMs: number (sólo response),
 *     detail:    objeto extra (payload, stack, etc.),
 *   }
 */

const MAX_ENTRIES = 500

const state = {
  seq: 1,
  entries: /** @type {Array<object>} */ ([]),
  listeners: /** @type {Set<Function>} */ (new Set()),
  installedHandlers: false,
  forwardToBackend: true,
}

// Redactar valores sensibles en payloads que loguea el wrapper de fetch.
const SENSITIVE_KEYS = /password|token|authorization|cookie|secret/i
function redact(value, depth = 0) {
  if (value == null) return value
  if (depth > 4) return '[deep]'
  if (typeof value === 'string') {
    return value.length > 500 ? value.slice(0, 500) + '…' : value
  }
  if (Array.isArray(value)) {
    return value.slice(0, 50).map((v) => redact(v, depth + 1))
  }
  if (typeof value === 'object') {
    const out = {}
    for (const [k, v] of Object.entries(value)) {
      out[k] = SENSITIVE_KEYS.test(k) ? '[redacted]' : redact(v, depth + 1)
    }
    return out
  }
  return value
}

function notify() {
  for (const fn of state.listeners) {
    try {
      fn(state.entries)
    } catch {
      /* no-op */
    }
  }
}

async function forwardToBackend(entry) {
  if (!state.forwardToBackend) return
  // No usamos apiCall acá para evitar un loop infinito (apiCall llama a log,
  // log llama a apiCall, etc.). Un fetch directo, silencioso si falla.
  try {
    const BASE = import.meta.env?.VITE_API_URL || ''
    await fetch(`${BASE}/api/dev/logs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        level: entry.level,
        phase: entry.phase,
        message: entry.message,
        component: entry.component,
        detail: entry.detail,
        path: entry.path,
        method: entry.method,
        status: entry.status,
        durationMs: entry.durationMs,
      }),
    })
  } catch {
    /* no-op: no queremos que un fallo de red rompa el log local */
  }
}

export function log(entry) {
  const record = {
    id: state.seq++,
    at: new Date().toISOString(),
    source: 'frontend',
    level: entry.level || 'info',
    phase: entry.phase || 'log',
    component: entry.component || 'app',
    ...entry,
  }
  state.entries.push(record)
  if (state.entries.length > MAX_ENTRIES) {
    state.entries.splice(0, state.entries.length - MAX_ENTRIES)
  }
  notify()
  // No re-forward los logs que vienen del propio POST /dev/logs para evitar
  // un loop. El wrapper de api.js se encarga de excluir esa ruta antes de
  // llamar a log(), así que acá simplemente reenviamos todo.
  forwardToBackend(record)
  return record
}

export const info = (msg, extra) => log({ level: 'info', message: msg, ...extra })
export const warn = (msg, extra) => log({ level: 'warn', message: msg, ...extra })
export const logError = (msg, extra) => log({ level: 'error', message: msg, ...extra })

export function getAll() {
  return state.entries
}

export function clear() {
  state.entries = []
  state.seq = 1
  notify()
}

export function subscribe(fn) {
  state.listeners.add(fn)
  fn(state.entries)
  return () => state.listeners.delete(fn)
}

export function setForwardToBackend(flag) {
  state.forwardToBackend = Boolean(flag)
}

/**
 * Install global handlers. Llamado una vez desde el bootstrap de la app.
 * Idempotente: múltiples llamadas son no-ops.
 */
export function installGlobalHandlers() {
  if (state.installedHandlers) return
  if (typeof window === 'undefined') return
  state.installedHandlers = true

  window.addEventListener('error', (evt) => {
    log({
      level: 'error',
      phase: 'error',
      component: 'window.error',
      message: evt.message || String(evt.error || 'window error'),
      detail: {
        filename: evt.filename,
        lineno: evt.lineno,
        colno: evt.colno,
        stack: evt.error?.stack
          ? String(evt.error.stack).split('\n').slice(0, 10)
          : undefined,
      },
    })
  })

  window.addEventListener('unhandledrejection', (evt) => {
    const reason = evt.reason
    log({
      level: 'error',
      phase: 'error',
      component: 'unhandledrejection',
      message: reason?.message || String(reason || 'unhandled rejection'),
      detail: {
        name: reason?.name,
        stack: reason?.stack
          ? String(reason.stack).split('\n').slice(0, 10)
          : undefined,
      },
    })
  })

  // Capturar console.error y console.warn — muchos warnings de React salen
  // por acá y ayudan a entender qué falló.
  const origError = console.error
  const origWarn = console.warn
  console.error = (...args) => {
    try {
      log({
        level: 'error',
        phase: 'console',
        component: 'console.error',
        message: args
          .map((a) => (a instanceof Error ? a.message : typeof a === 'string' ? a : JSON.stringify(redact(a))))
          .join(' '),
        detail: { args: redact(args) },
      })
    } catch {
      /* no-op */
    }
    origError.apply(console, args)
  }
  console.warn = (...args) => {
    try {
      log({
        level: 'warn',
        phase: 'console',
        component: 'console.warn',
        message: args
          .map((a) => (typeof a === 'string' ? a : JSON.stringify(redact(a))))
          .join(' '),
        detail: { args: redact(args) },
      })
    } catch {
      /* no-op */
    }
    origWarn.apply(console, args)
  }
}

export { redact as _redact }
